import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Email,
  VerifiedUser
} from '@mui/icons-material';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { AutoCloseAlert, SuccessPopup } from '../../Common';
import { masterDataConfigAPI } from '../../../services/api'; // Add this import
import useRouteMeta from '../../../hooks/useRouteMeta';

const EmailVerification = () => {
  // SEO Meta Tags - Automatically applied from config
  useRouteMeta();
  
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30); // Start with 30 seconds countdown
  const [canResend, setCanResend] = useState(false); // Initially disable resend button
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [starterPackage, setStarterPackage] = useState({
    name: 'Starter',
    credits: 1500,
    validity_days: 30,
    description: 'Free starter package with 1500 tokens valid for 30 days'
  }); // Add state for starter package information

  const { verifyOTP, resendOTP, merchant } = useMerchantAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email;

  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
    
    // Fetch starter package information from API when component loads
    const fetchStarterPackage = async () => {
      try {
        const response = await masterDataConfigAPI.getPublicStarterPackage();
        if (response.data && response.data.starter_package) {
          setStarterPackage(response.data.starter_package);
        }
      } catch (error) {
        console.error('Failed to fetch starter package:', error);
        // Keep default values if fetch fails
      }
    };
    
    fetchStarterPackage();
  }, [email, navigate]);

  // Timer effect for resend OTP - modified to handle initial countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true); // Enable resend button when timer reaches 0
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  useEffect(() => {
    const isMobile = window.innerWidth < 600; // xs breakpoint
    
    if (isMobile) {
      document.body.style.backgroundColor = 'white';
    } else {
      document.body.style.backgroundColor = '#f5f5f5'; // or whatever your default background color is
    }

    // Cleanup function to restore original background when component unmounts
    return () => {
      document.body.style.backgroundColor = '#f5f5f5'; // restore default
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }
    
    // Validate OTP length (4 digits)
    if (otp.trim().length !== 4) {
      setError('Please enter a valid 4-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await verifyOTP(email, otp);
      
      if (result.success) {
        setSuccess(result.message);
        // Show success popup instead of auto-navigating
        setShowSuccessPopup(true);
      } else {
        setError(result.error || 'Invalid or expired OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    setError('');

    try {
      const result = await resendOTP(email);
      
      if (result.success) {
        setSuccess(result.message);
        // Start 30 second timer after successful OTP resend
        setResendTimer(30);
        setCanResend(false); // Disable resend button during countdown
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  const handleNavigateToDashboard = () => {
    setShowSuccessPopup(false);
    // Automatically log in the user and navigate to dashboard
    navigate('/dashboard');
  };

  const handleNavigateToGenerate = () => {
    setShowSuccessPopup(false);
    // Navigate directly to the AI generation wizard
    navigate('/generate');
  };

  // New accounts await admin verification before free tokens — offer plans instead of generating
  const handleNavigateToPlans = () => {
    setShowSuccessPopup(false);
    navigate('/credits');
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    // Default to dashboard if user closes popup or clicks outside
    navigate('/dashboard');
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: { xs: 0.5, sm: 8 }, // Further reduced top margin for mobile
          marginBottom: { xs: 1, sm: 0 }, // Reduced bottom margin for mobile
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Verification Form */}
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 2, sm: 4 }, // Further reduced padding for mobile
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '18px', // Changed from 2 to 18px
            width: '100%',
            boxShadow: { xs: 'none', sm: 3 }, // No shadow for mobile, shadow for larger screens
            border: { xs: 'none', sm: '1px solid #E0E0E0' }, // No border on mobile, border on larger screens
            '@media (min-width: 414px)': {
              padding: 3, // Larger padding for 414px+ devices
            }
          }}
        >
          {/* Glammify Logo - moved inside verification form */}
          <Box
            component="img"
            src="/assets/img/glammify-logo.png" 
            alt="Glammify Logo" 
            sx={{
              height: 'auto',
              maxWidth: { xs: '150px', sm: '300px' },
              width: '100%',
              marginBottom: { xs: '40px', sm: '40px' },
              marginTop: { xs: '15px', sm: '25px' }
            }}
          />

          <VerifiedUser sx={{ 
            fontSize: { xs: 30, sm: 64 }, 
            color: '#6318C6', 
            mb: { xs: 1, sm: 2 } 
          }} />
          
          <Typography variant="h5" sx={{ 
            mb: { xs: 1, sm: 2 }, 
            textAlign: 'center',
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}>
            Check Your Email
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ 
            mb: { xs: 2, sm: 3 }, 
            textAlign: 'center',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}>
            We've sent a 4-digit verification code to
          </Typography>
          
          <Typography variant="subtitle1" sx={{ 
            mb: { xs: 2, sm: 3 }, 
            fontWeight: 'bold',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}>
            {email}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <AutoCloseAlert
              severity="success"
              message={success}
              onClose={() => setSuccess('')}
            />
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              id="otp"
              label="Enter 4-digit OTP"
              name="otp"
              autoComplete="one-time-code"
              autoFocus
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              disabled={loading}
              inputProps={{ 
                maxLength: 4,
                style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem', height: '1em' }
              }}
              sx={{
                width: '280px', // Reduced width
                mx: 'auto', // Center horizontally
                display: 'block', // Ensure block display for centering
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6318C6',
                  },
                  backgroundColor: 'white', // Force white background
                  '&.Mui-filled': {
                    backgroundColor: 'white', // Override filled state background
                  },
                  '&:hover': {
                    backgroundColor: 'white', // Keep white on hover
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                    WebkitTextFillColor: 'inherit',
                  },
                  '& input:-internal-autofill-selected': {
                    backgroundColor: 'white !important',
                  },
                },
                '& .MuiInputLabel-root': {
                  textAlign: 'center',
                  width: '100%',
                  backgroundColor: 'white',
                  px: 1,
                  '&.Mui-focused': {
                    color: '#6318C6',
                  },
                },
                '& .MuiInputLabel-shrink': {
                  textAlign: 'center',
                  backgroundColor: 'white',
                  px: 1,
                },
              }}
            />
            
            <Button
              type="submit"
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                width: '280px', // Reduced width
                mx: 'auto', // Center horizontally
                display: 'block', // Ensure block display for centering
                background: '#6318C6',
                '&:hover': {
                  background: '#5a4db2',
                }
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Verifying...
                </>
              ) : (
                'Verify Email'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ 
              mb: 1,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}>
              Didn't receive the code?
            </Typography>
              <Button
                variant={!canResend ? "outlined" : "text"}
                onClick={handleResendOTP}
                disabled={resendLoading || !canResend}
                sx={{
                  color: canResend ? '#6318C6' : '#ff0000',
                  minWidth: '120px',
                  opacity: canResend ? 1 : 0.8,
                  cursor: canResend ? 'pointer' : 'not-allowed',
                  '&.Mui-disabled': {
                    color: '#ff0000 !important',
                    border: 'none !important'
                  },
                  '&:disabled': {
                    color: '#ff0000 !important',
                    border: 'none !important'
                  }
                }}
              >
                {resendLoading ? (
                  <CircularProgress size={16} />
                ) : !canResend ? (
                  `Resend in ${resendTimer}s`
                ) : (
                  'Resend OTP'
                )}
              </Button>

            </Box>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: { xs: 1, sm: 4 }, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}>
            Check your spam folder if you don't see the email
          </Typography>
        </Box>
      </Box>

      {/* Success Popup */}
      <SuccessPopup
        open={showSuccessPopup}
        onClose={handleCloseSuccessPopup}
        onNavigateToDashboard={handleNavigateToDashboard}
        onNavigateToGenerate={handleNavigateToGenerate}
        onNavigateToPlans={handleNavigateToPlans}
        verificationStatus={merchant?.verification_status}
        starterPackage={starterPackage} // Use the dynamic starter package from API
      />
    </Container>
  );
};

export default EmailVerification; 